<template>
  <NuxtLayout navbar-color="#F9F8F5">
    <div class="job">
      <section class="job__overview">
        <div class="container">
          <div class="job__grid">
            <div class="job__content">
              <p class="job__department">
                {{ positionData?.department }}
              </p>
              <h1 class="heading--h1 job__name">{{ positionData?.title }}</h1>
              <p class="job__intro">
                We're a remote-first company that gathers folks passionate about
                redesigning healthcare for millions of people on the planet. If
                you are led by an ambition to make a good impact in the
                surrounding world - we would love to meet you.
              </p>
              <CareersDetailsCard
                v-if="positionData"
                :data="positionData"
                class="job__card"
              />
              <div class="job__sections" v-if="parsedDescription">
                <section
                  class="job__section"
                  v-for="(content, listing) in parsedDescription"
                  :key="listing"
                >
                  <h3>{{ capitalizeString(listing) }}</h3>
                  <div v-html="content"></div>
                </section>
              </div>
            </div>
            <CareersDetailsCard
              v-if="positionData"
              :data="positionData"
              class="job__card--desktop"
            />
          </div>
        </div>
      </section>
      <StoryblokComponent v-if="story" :blok="story.content" />
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import ogImage from '@/assets/images/og-careers.png'
import { capitalizeString } from '@/utils/helpers'
import type { JobDataSummary } from '@/types'

const { params } = useRoute()

const parsedDescription = ref({})

const [{ story }, { data: positionData }] = await Promise.all([
  useFetchStory('global/careers-details'),
  useFetch<JobDataSummary>('/api/careers/postings/' + params.id),
])

if (!positionData.value?.status) {
  throw createError({
    message: 'Position not found',
    statusCode: 404,
  })
}

const parseJobPostingDescription = (
  html: string,
): { [key: string]: string } => {
  const result: { [key: string]: string } = {}
  const sections = html.split(/<strong>(.*?)<\/strong>/)

  for (let i = 1; i < sections.length; i += 2) {
    const sectionName = sections[i]
      .toLowerCase()
      .replace(/:/g, '')
      .replace(/' '/g, '-')
      .trim()
    const sectionContent = sections[i + 1].trim()
    result[sectionName] = sectionContent
  }

  return result
}

useSeoMeta({
  title: positionData.value.title,
  ogTitle: positionData.value.title,
  description: `Careers at infermedica - ${positionData.value.title}`,
  ogDescription: `Careers at infermedica - ${positionData.value.title}`,
  ogImage: ogImage,
})

onMounted(() => {
  if (!positionData.value) return

  parsedDescription.value = parseJobPostingDescription(
    positionData.value.description,
  )
})
</script>

<style lang="scss" scoped>
.job {
  &__section {
    margin-bottom: 56px;

    @media (min-width: $breakpoint-lg) {
      margin-bottom: 96px;
    }

    & :deep(p) {
      margin: 0;
    }

    & :deep(ul) {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    & :deep(li) {
      display: flex;
      align-items: flex-start;

      &::before {
        @include icon('@/assets/icons/list-tick-cobalt.svg', 15px, 14px);

        content: '';
        flex-shrink: 0;
        position: relative;
        top: 8px;
        display: block;
        margin-right: 10px;
      }
    }
  }

  &__department {
    @include heading-subheading;

    margin-bottom: 20px;
  }

  &__overview {
    padding: 56px 0;
    background-color: var(--paper-100);

    @media (min-width: $breakpoint-lg) {
      padding: 96px 0;
    }
  }

  &__subheading {
    margin-bottom: 20px;
  }

  &__name {
    @include heading-3;

    margin-bottom: 20px;

    @media (min-width: $breakpoint-md) {
      @include heading-1;

      margin-bottom: 32px;
    }
  }

  &__intro {
    margin-bottom: 40px;

    @media (min-width: $breakpoint-lg) {
      margin-bottom: 64px;
    }
  }

  &__section-heading {
    @include heading-4;

    margin-bottom: 22px;

    @media (min-width: $breakpoint-md) {
      @include heading-3;

      margin-bottom: 32px;
    }
  }

  &__requirement-name {
    margin-bottom: 16px;
  }

  &__requirement-point-list {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    margin-bottom: 40px;

    @media (min-width: $breakpoint-md) {
      margin-bottom: 52px;
    }
  }

  &__responsibilities {
    margin-bottom: 64px;

    @media (min-width: $breakpoint-md) {
      margin-bottom: 100px;
    }
  }

  &__responsibility-name {
    margin-bottom: 12px;
  }

  &__card {
    display: block;
    margin-bottom: 56px;

    &--desktop {
      display: none;
      position: sticky;
      top: 140px;
      margin-bottom: 24px;
      right: 0;

      @media (min-width: $breakpoint-lg) {
        display: block;
      }
    }

    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 85px;
    align-items: flex-start;

    @media (min-width: $breakpoint-lg) {
      grid-template-columns: 574px 1fr;
    }
  }

  &__bar {
    display: block;

    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  &__description {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
  }
}
</style>
